import type { Id } from "../../../convex/_generated/dataModel";
import { addDays, localDayStartMs } from "../../lib/dayHelpers";
import type { LoggingFlowPayload } from "../../lib/logging-flow";
import { DATA_SOURCE_LABELS } from "../SourceChip";

// Unified Meal Review pure logic (plan todo 12). Everything interactive about
// the review screen funnels through this module so the repo's node-env test
// pattern (renderToStaticMarkup + pure functions, NO jsdom) can assert every
// behavior: totals unknown rules, style friction mapping, hidden-ingredient
// seeding, and the exact logMealBatch payload.
//
// CONTRACT for todos 13-18 (capture flows) and 23 (chat cards):
// startFlow("review", payload) where payload is a MealReviewPayload —
// LoggingFlowPayload plus optional extras below. Unknown nutrition stays
// undefined everywhere; NOTHING in this module ever zero-fills.

export type MealType = "breakfast" | "lunch" | "dinner" | "snack";
export type LoggingStyle = "fastest" | "balanced" | "accurate";

// Analysis-provided calorie range (todos 13-18 attach this after analyze*).
export interface MealReviewUncertainty {
  readonly lowKcal: number;
  readonly highKcal: number;
  readonly reason: string;
}

// Review payload = the todo-10 LoggingFlowPayload plus optional extras the
// capture flows may attach. All extras are optional so every existing
// LoggingFlowPayload (e.g. buildPlateReviewPayload) is already valid. The
// LoggingFlowPayload uncertainty (todo 13, optional numeric fields) is
// narrowed to this complete shape via toMealReviewPayload at the host.
export interface MealReviewPayload extends Omit<LoggingFlowPayload, "uncertainty"> {
  readonly uncertainty?: MealReviewUncertainty;
  readonly date?: number; // preselected day (epoch-ms); defaults to today
  readonly mealType?: string; // flow's guess; invalid values fall back to time-of-day
  readonly name?: string; // suggested meal name
}

// Narrows a flow payload for the review screen: an uncertainty range is kept
// only when every display field is present (incomplete ranges are dropped so
// the banner never renders NaN). Everything else passes through untouched.
export function toMealReviewPayload(payload: LoggingFlowPayload): MealReviewPayload {
  const uncertainty = payload.uncertainty;
  if (
    uncertainty !== undefined &&
    typeof uncertainty.lowKcal === "number" &&
    typeof uncertainty.highKcal === "number" &&
    typeof uncertainty.reason === "string"
  ) {
    return {
      ...payload,
      uncertainty: {
        lowKcal: uncertainty.lowKcal,
        highKcal: uncertainty.highKcal,
        reason: uncertainty.reason,
      },
    };
  }
  const { uncertainty: _incompleteUncertainty, ...rest } = payload;
  return rest;
}

// One editable review row. `key` is a local temp id (Date.now()+Math.random
// pattern — NEVER crypto.randomUUID, it crashes non-HTTPS WebViews).
export interface ReviewItem {
  readonly key: string;
  readonly name: string;
  readonly quantity?: number;
  readonly unit?: string;
  readonly calories?: number;
  readonly proteinG?: number;
  readonly carbsG?: number;
  readonly fatG?: number;
  readonly fiberG?: number;
  readonly dataSource?: string;
  readonly confidence?: string;
}

export type ReviewItemPatch = Partial<Omit<ReviewItem, "key">>;

export function makeReviewKey(): string {
  return `review_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

// ---------------------------------------------------------------------------
// Totals — aligned with plateTotals semantics (src/lib/plate.tsx): sums only
// defined values, a nutrient total is present only when at least one item
// defines it, and hasUnknown flags any item with unknown calories.
// ---------------------------------------------------------------------------

export interface ReviewTotals {
  readonly calories?: number;
  readonly proteinG?: number;
  readonly carbsG?: number;
  readonly fatG?: number;
  readonly fiberG?: number;
  readonly hasUnknown: boolean;
}

export function reviewTotals(items: readonly ReviewItem[]): ReviewTotals {
  let calories: number | undefined;
  let proteinG: number | undefined;
  let carbsG: number | undefined;
  let fatG: number | undefined;
  let fiberG: number | undefined;
  let hasUnknown = false;
  for (const item of items) {
    if (item.calories === undefined) {
      hasUnknown = true;
    } else {
      calories = (calories ?? 0) + item.calories;
    }
    if (item.proteinG !== undefined) proteinG = (proteinG ?? 0) + item.proteinG;
    if (item.carbsG !== undefined) carbsG = (carbsG ?? 0) + item.carbsG;
    if (item.fatG !== undefined) fatG = (fatG ?? 0) + item.fatG;
    if (item.fiberG !== undefined) fiberG = (fiberG ?? 0) + item.fiberG;
  }
  return { calories, proteinG, carbsG, fatG, fiberG, hasUnknown };
}

// ---------------------------------------------------------------------------
// Per-item completeness (drives "accurate" style portion warnings + badges).
// ---------------------------------------------------------------------------

export interface ItemCompleteness {
  readonly hasName: boolean;
  readonly hasQuantity: boolean; // quantity defined (drives the accurate "Add quantity" cue)
  readonly hasPortion: boolean; // quantity AND unit both defined
  readonly hasCalories: boolean;
  readonly knownNutrients: number; // 0..5 across kcal/protein/carbs/fat/fibre
  readonly complete: boolean; // name + portion + all five nutrients
}

export function itemCompleteness(item: ReviewItem): ItemCompleteness {
  const hasName = item.name.trim().length > 0;
  const hasQuantity = item.quantity !== undefined;
  const hasPortion =
    hasQuantity && item.unit !== undefined && item.unit.trim().length > 0;
  const hasCalories = item.calories !== undefined;
  const knownNutrients = [item.calories, item.proteinG, item.carbsG, item.fatG, item.fiberG].filter(
    (value) => value !== undefined,
  ).length;
  return {
    hasName,
    hasQuantity,
    hasPortion,
    hasCalories,
    knownNutrients,
    complete: hasName && hasPortion && knownNutrients === 5,
  };
}

export interface ReviewCompleteness {
  readonly totalItems: number;
  readonly completeItems: number;
  // Rounded 0-100 share of items with full macros; 0 when there are no items.
  readonly percent: number;
}

export function reviewCompleteness(items: readonly ReviewItem[]): ReviewCompleteness {
  const completeItems = items.filter((item) => itemCompleteness(item).complete).length;
  return {
    totalItems: items.length,
    completeItems,
    percent: items.length === 0 ? 0 : Math.round((completeItems / items.length) * 100),
  };
}

// ---------------------------------------------------------------------------
// loggingStyle friction mapping (spec section 11). getProfile.loggingStyle may
// be undefined -> treated as "balanced".
// ---------------------------------------------------------------------------

export function normalizeLoggingStyle(style: string | null | undefined): LoggingStyle {
  return style === "fastest" || style === "accurate" ? style : "balanced";
}

export interface ReviewFriction {
  readonly style: LoggingStyle;
  // Hidden-ingredient prompt starts expanded (still gated on analysis source).
  readonly hiddenIngredientsAutoExpand: boolean;
  // "accurate": per-item portion/unit completion warnings.
  readonly portionWarnings: boolean;
  // "accurate": data-completeness badges on items + totals.
  readonly completenessBadge: boolean;
  // "accurate": "weigh repeated meals" hint for memory-sourced items.
  readonly weighHint: boolean;
  // "fastest": item nutrition details stay collapsed behind one tap.
  readonly collapseItemDetails: boolean;
  // "fastest": single prominent Log button in the footer.
  readonly prominentLogButton: boolean;
}

export function reviewFrictionForStyle(style: string | null | undefined): ReviewFriction {
  const normalized = normalizeLoggingStyle(style);
  return {
    style: normalized,
    hiddenIngredientsAutoExpand: normalized !== "fastest",
    portionWarnings: normalized === "accurate",
    completenessBadge: normalized === "accurate",
    weighHint: normalized === "accurate",
    collapseItemDetails: normalized === "fastest",
    prominentLogButton: normalized === "fastest",
  };
}

// Sources produced by AI analysis flows (photo capture/upload, text, voice).
const ANALYSIS_SOURCES: ReadonlySet<string> = new Set(["photo", "upload", "text", "voice"]);

// Hidden-ingredient prompt opens on its own only for analysis flows and only
// when the user did NOT choose the fastest style (plan section 11 matrix).
export function hiddenIngredientsDefaultOpen(
  source: string | undefined,
  style: string | null | undefined,
): boolean {
  return (
    source !== undefined &&
    ANALYSIS_SOURCES.has(source) &&
    reviewFrictionForStyle(style).hiddenIngredientsAutoExpand
  );
}

// Fastest-style severe-uncertainty predicate (plan todo 26): the prompt is
// worth its friction only when the analysis returned a calorie range OR any
// item is low confidence.
export function severeUncertaintyPresent(
  hasRange: boolean,
  items: readonly ReviewItem[],
): boolean {
  return hasRange || items.some((item) => item.confidence === "low");
}

// Whether the hidden-ingredient prompt renders at all. Balanced and accurate
// always show it (current default behavior); fastest shows it only under
// severe uncertainty.
export function hiddenIngredientsVisible(
  style: string | null | undefined,
  context: { readonly hasRange: boolean; readonly items: readonly ReviewItem[] },
): boolean {
  if (reviewFrictionForStyle(style).style !== "fastest") return true;
  return severeUncertaintyPresent(context.hasRange, context.items);
}

// Accurate-style "weigh repeated meals" hint trigger: any item sourced from
// the user's food memory.
export function hasMemorySourcedItem(items: readonly ReviewItem[]): boolean {
  return items.some((item) => item.dataSource === "memory");
}

// ---------------------------------------------------------------------------
// Hidden-ingredient chips: seed name + typical quantity/unit ONLY. Calories
// and macros stay UNDEFINED (unknown, never an uncited default number),
// confidence "low", dataSource "user-entered".
// ---------------------------------------------------------------------------

export interface HiddenIngredientChip {
  readonly id: string;
  readonly label: string;
  readonly quantity?: number;
  readonly unit?: string;
}

export const HIDDEN_INGREDIENT_CHIPS: readonly HiddenIngredientChip[] = [
  { id: "oil", label: "Oil", quantity: 1, unit: "tbsp" },
  { id: "ghee", label: "Ghee", quantity: 1, unit: "tsp" },
  { id: "butter", label: "Butter", quantity: 1, unit: "tbsp" },
  { id: "sauce", label: "Sauce", quantity: 1, unit: "tbsp" },
  { id: "dressing", label: "Dressing", quantity: 1, unit: "tbsp" },
  { id: "sugar", label: "Sugar", quantity: 1, unit: "tsp" },
  { id: "drink", label: "Drink", quantity: 1, unit: "glass" },
  { id: "other", label: "Other" },
];

export function hiddenIngredientItem(chipId: string): ReviewItem | null {
  const chip = HIDDEN_INGREDIENT_CHIPS.find((entry) => entry.id === chipId);
  if (chip === undefined) return null;
  return {
    key: makeReviewKey(),
    name: chip.label,
    ...(chip.quantity === undefined ? {} : { quantity: chip.quantity }),
    ...(chip.unit === undefined ? {} : { unit: chip.unit }),
    dataSource: "user-entered",
    confidence: "low",
  };
}

// ---------------------------------------------------------------------------
// Source + confidence labels and mappings.
// ---------------------------------------------------------------------------

// SourceChip display labels (contractual, plan todo 12). The canonical map
// lives in src/components/SourceChip.tsx; this re-export keeps the existing
// review-utils API stable for consumers and tests.
export { DATA_SOURCE_LABELS, dataSourceLabel } from "../SourceChip";

// Logging-source chip (which capture flow produced this review).
const SOURCE_CHIP_LABELS: Record<string, string> = {
  photo: "Photo",
  upload: "Photo",
  text: "Text description",
  voice: "Voice",
  barcode: "Barcode",
  label: "Nutrition label",
  search: "Food search",
  quickAdd: "Quick add",
  plate: "Plate",
  chat: "Chat",
  library: "Library",
  recent: "Recent",
};

export function sourceChipLabel(source: string | undefined): string {
  if (source === undefined) return "Manual";
  return SOURCE_CHIP_LABELS[source] ?? "Manual";
}

// ConfidenceChip (progress/components) speaks high/moderate/low; review items
// may carry "medium" from the backend contract.
export function confidenceChipLevel(confidence: string | undefined): string | null {
  switch (confidence) {
    case "high":
    case "low":
      return confidence;
    case "medium":
    case "moderate":
      return "moderate";
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Meal type + date/time helpers.
// ---------------------------------------------------------------------------

export function normalizeMealType(value: string | undefined): MealType | null {
  return value === "breakfast" || value === "lunch" || value === "dinner" || value === "snack"
    ? value
    : null;
}

// Time-of-day fallback when the flow did not guess a meal type.
export function defaultMealType(hour: number): MealType {
  if (hour >= 5 && hour < 11) return "breakfast";
  if (hour >= 11 && hour < 16) return "lunch";
  if (hour >= 16 && hour < 21) return "dinner";
  return "snack";
}

export function formatTimeHHMM(ts: number): string {
  const date = new Date(ts);
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

// Combines the selected day (local day-start ms) with the "HH:MM" time control
// into the clientLoggedAt epoch-ms emitted in the logMealBatch payload.
// Invalid time strings fall back to the day start (documented, deterministic).
export function combineDateAndTime(dayStartMs: number, timeHHMM: string): number {
  const match = /^(\d{1,2}):(\d{2})$/.exec(timeHHMM.trim());
  if (match === null) return dayStartMs;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return dayStartMs;
  return dayStartMs + hours * 3_600_000 + minutes * 60_000;
}

// Unit editor options (free text remains possible via the datalist input).
export const REVIEW_UNIT_OPTIONS: readonly string[] = [
  "g",
  "ml",
  "cup",
  "piece",
  "tbsp",
  "tsp",
  "serving",
  "bowl",
  "plate",
  "glass",
];

// Blank -> undefined (unknown); otherwise a finite non-negative number or
// undefined. Shared by every numeric editor so "" never becomes 0.
export function parseOptionalMetric(value: string): number | undefined {
  if (value.trim().length === 0) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
}

// "2 tbsp" -> { quantity: 2, unit: "tbsp" } for memory/custom-food seeding.
export function parseServingSize(servingSize: string | undefined): {
  readonly quantity?: number;
  readonly unit?: string;
} {
  if (servingSize === undefined) return {};
  const match = /^\s*(\d+(?:\.\d+)?)\s+(.+)$/.exec(servingSize.trim());
  if (match === null) return {};
  const quantity = Number(match[1]);
  const unit = (match[2] ?? "").trim();
  return {
    ...(Number.isFinite(quantity) && quantity > 0 ? { quantity } : {}),
    ...(unit.length > 0 ? { unit } : {}),
  };
}

// ---------------------------------------------------------------------------
// "Add ingredient" mini-search mappers (memories + custom foods).
// ---------------------------------------------------------------------------

export interface MemoryLike {
  readonly foodName: string;
  readonly calories: number;
  readonly servingSize?: string;
  readonly proteinG?: number;
  readonly carbsG?: number;
  readonly fatG?: number;
  readonly fiberG?: number;
}

export function reviewItemFromMemory(memory: MemoryLike): ReviewItem {
  return {
    key: makeReviewKey(),
    name: memory.foodName,
    ...parseServingSize(memory.servingSize),
    calories: memory.calories,
    ...(memory.proteinG === undefined ? {} : { proteinG: memory.proteinG }),
    ...(memory.carbsG === undefined ? {} : { carbsG: memory.carbsG }),
    ...(memory.fatG === undefined ? {} : { fatG: memory.fatG }),
    ...(memory.fiberG === undefined ? {} : { fiberG: memory.fiberG }),
    dataSource: "memory",
    confidence: "high",
  };
}

export interface CustomFoodLike {
  readonly name: string;
  readonly calories?: number;
  readonly servingSize?: string;
  readonly proteinG?: number;
  readonly carbsG?: number;
  readonly fatG?: number;
  readonly fiberG?: number;
  readonly source?: string;
}

const CUSTOM_FOOD_DATA_SOURCES: Record<string, string> = {
  manual: "user-entered",
  label: "nutrition-label",
  barcode: "barcode",
};

export function reviewItemFromCustomFood(food: CustomFoodLike): ReviewItem {
  return {
    key: makeReviewKey(),
    name: food.name,
    ...parseServingSize(food.servingSize),
    ...(food.calories === undefined ? {} : { calories: food.calories }),
    ...(food.proteinG === undefined ? {} : { proteinG: food.proteinG }),
    ...(food.carbsG === undefined ? {} : { carbsG: food.carbsG }),
    ...(food.fatG === undefined ? {} : { fatG: food.fatG }),
    ...(food.fiberG === undefined ? {} : { fiberG: food.fiberG }),
    dataSource:
      food.source !== undefined
        ? (CUSTOM_FOOD_DATA_SOURCES[food.source] ?? "user-entered")
        : "user-entered",
  };
}

// ---------------------------------------------------------------------------
// Review screen state + reducer (the interactivity seam for tests).
// ---------------------------------------------------------------------------

export interface RemovedReviewItem {
  readonly item: ReviewItem;
  readonly index: number;
}

export interface MealReviewState {
  readonly name: string;
  readonly mealType: MealType;
  readonly date: number; // local day-start ms
  readonly time: string; // "HH:MM" for the logged-at control
  readonly saveAsMemory: boolean;
  readonly items: readonly ReviewItem[];
  readonly removed: RemovedReviewItem | null;
}

export type MealReviewAction =
  | { readonly type: "setName"; readonly name: string }
  | { readonly type: "setMealType"; readonly mealType: MealType }
  | { readonly type: "stepDate"; readonly days: number }
  | { readonly type: "setTime"; readonly time: string }
  | { readonly type: "toggleSaveAsMemory" }
  | { readonly type: "addItem"; readonly item: ReviewItem }
  | { readonly type: "addHiddenIngredient"; readonly chipId: string }
  | { readonly type: "updateItem"; readonly key: string; readonly patch: ReviewItemPatch }
  | { readonly type: "removeItem"; readonly key: string }
  | { readonly type: "undoRemove" }
  | { readonly type: "dismissUndo" };

export function initReviewState(payload: MealReviewPayload, now: number): MealReviewState {
  const items: ReviewItem[] = (payload.items ?? []).map((item) => ({
    key: makeReviewKey(),
    name: item.name,
    ...(item.quantity === undefined ? {} : { quantity: item.quantity }),
    ...(item.unit === undefined ? {} : { unit: item.unit }),
    ...(item.calories === undefined ? {} : { calories: item.calories }),
    ...(item.proteinG === undefined ? {} : { proteinG: item.proteinG }),
    ...(item.carbsG === undefined ? {} : { carbsG: item.carbsG }),
    ...(item.fatG === undefined ? {} : { fatG: item.fatG }),
    ...(item.fiberG === undefined ? {} : { fiberG: item.fiberG }),
    ...(item.dataSource === undefined ? {} : { dataSource: item.dataSource }),
    ...(item.confidence === undefined ? {} : { confidence: item.confidence }),
  }));
  return {
    name: payload.name ?? "",
    mealType: normalizeMealType(payload.mealType) ?? defaultMealType(new Date(now).getHours()),
    date: localDayStartMs(payload.date ?? now),
    time: formatTimeHHMM(now),
    saveAsMemory: false,
    items,
    removed: null,
  };
}

export function reviewReducer(state: MealReviewState, action: MealReviewAction): MealReviewState {
  switch (action.type) {
    case "setName":
      return { ...state, name: action.name };
    case "setMealType":
      return { ...state, mealType: action.mealType };
    case "stepDate":
      return { ...state, date: addDays(state.date, action.days) };
    case "setTime":
      return { ...state, time: action.time };
    case "toggleSaveAsMemory":
      return { ...state, saveAsMemory: !state.saveAsMemory };
    case "addItem":
      return { ...state, items: [...state.items, action.item] };
    case "addHiddenIngredient": {
      const seeded = hiddenIngredientItem(action.chipId);
      if (seeded === null) return state;
      return { ...state, items: [...state.items, seeded] };
    }
    case "updateItem":
      return {
        ...state,
        items: state.items.map((item) =>
          item.key === action.key ? { ...item, ...action.patch, key: item.key } : item,
        ),
      };
    case "removeItem": {
      const index = state.items.findIndex((item) => item.key === action.key);
      if (index < 0) return state;
      const item = state.items[index];
      if (item === undefined) return state;
      return {
        ...state,
        items: state.items.filter((entry) => entry.key !== action.key),
        removed: { item, index },
      };
    }
    case "undoRemove": {
      if (state.removed === null) return state;
      const items = [...state.items];
      items.splice(Math.min(state.removed.index, items.length), 0, state.removed.item);
      return { ...state, items, removed: null };
    }
    case "dismissUndo":
      return { ...state, removed: null };
  }
}

// ---------------------------------------------------------------------------
// logMealBatch payload construction (EXACT shape — convex/lib/mealValidators
// batchArgs). Unknown fields are OMITTED (undefined, never 0).
// ---------------------------------------------------------------------------

export type BatchSource =
  | "photo" | "text" | "web" | "correction" | "memory" | "manual"
  | "barcode" | "label" | "search" | "voice" | "recipe" | "custom" | "recent";

export type BatchDataSource =
  | "verified-database" | "barcode" | "nutrition-label" | "ai-estimate"
  | "user-entered" | "saved-recipe" | "memory" | "incomplete";

export type BatchConfidence = "high" | "medium" | "low";

const BATCH_SOURCES: ReadonlySet<string> = new Set([
  "photo", "text", "web", "correction", "memory", "manual",
  "barcode", "label", "search", "voice", "recipe", "custom", "recent",
]);

// Maps the review flow source onto the batch source union; anything unknown
// (e.g. "plate", "quickAdd", "chat") falls back to "manual".
const FLOW_SOURCE_TO_BATCH: Record<string, BatchSource> = {
  photo: "photo",
  upload: "photo",
  text: "text",
  voice: "voice",
  barcode: "barcode",
  label: "label",
  search: "search",
  memory: "memory",
  recipe: "recipe",
  recent: "recent",
  web: "web",
  correction: "correction",
  custom: "custom",
};

export function mapLogSource(source: string | undefined): BatchSource {
  if (source === undefined) return "manual";
  const mapped = FLOW_SOURCE_TO_BATCH[source];
  if (mapped !== undefined) return mapped;
  return BATCH_SOURCES.has(source) ? (source as BatchSource) : "manual";
}

export function mapBatchDataSource(dataSource: string | undefined): BatchDataSource | undefined {
  if (dataSource === undefined) return undefined;
  return dataSource in DATA_SOURCE_LABELS ? (dataSource as BatchDataSource) : undefined;
}

export function mapBatchConfidence(confidence: string | undefined): BatchConfidence | undefined {
  switch (confidence) {
    case "high":
    case "low":
      return confidence;
    case "medium":
    case "moderate":
      return "medium";
    default:
      return undefined;
  }
}

export interface LogMealBatchItem {
  name: string;
  quantity?: number;
  unit?: string;
  estimatedKcal?: number;
  finalKcal?: number;
  proteinG?: number;
  carbsG?: number;
  fatG?: number;
  fiberG?: number;
  confidence?: BatchConfidence;
  dataSource?: BatchDataSource;
  notes?: string;
  kcalUnknown?: boolean;
}

export interface LogMealBatchArgs {
  date: number;
  clientDayStart: number;
  clientLoggedAt: number;
  mealType: MealType;
  name?: string;
  imageStorageId?: Id<"_storage">;
  source: BatchSource;
  saveAsMemory?: boolean;
  items: LogMealBatchItem[];
}

export interface ReviewSubmission {
  readonly date: number; // local day-start ms
  readonly loggedAt: number; // from combineDateAndTime(date, time)
  readonly mealType: MealType;
  readonly name?: string;
  readonly imageStorageId?: Id<"_storage">;
  readonly source?: string;
  readonly saveAsMemory: boolean;
  readonly items: readonly ReviewItem[];
}

export function buildLogMealBatchArgs(input: ReviewSubmission): LogMealBatchArgs {
  const items: LogMealBatchItem[] = input.items.map((item) => {
    const confidence = mapBatchConfidence(item.confidence);
    const dataSource = mapBatchDataSource(item.dataSource);
    return {
      name: item.name.trim(),
      ...(item.quantity === undefined ? {} : { quantity: item.quantity }),
      ...(item.unit === undefined || item.unit.trim().length === 0
        ? {}
        : { unit: item.unit.trim() }),
      // Unknown calories -> kcalUnknown flag; NEVER a fabricated 0.
      ...(item.calories === undefined
        ? { kcalUnknown: true }
        : { estimatedKcal: item.calories, finalKcal: item.calories }),
      ...(item.proteinG === undefined ? {} : { proteinG: item.proteinG }),
      ...(item.carbsG === undefined ? {} : { carbsG: item.carbsG }),
      ...(item.fatG === undefined ? {} : { fatG: item.fatG }),
      ...(item.fiberG === undefined ? {} : { fiberG: item.fiberG }),
      ...(confidence === undefined ? {} : { confidence }),
      ...(dataSource === undefined ? {} : { dataSource }),
    };
  });
  const trimmedName = input.name?.trim() ?? "";
  return {
    date: input.date,
    clientDayStart: localDayStartMs(input.date),
    clientLoggedAt: input.loggedAt,
    mealType: input.mealType,
    ...(trimmedName.length > 0 ? { name: trimmedName } : {}),
    ...(input.imageStorageId === undefined ? {} : { imageStorageId: input.imageStorageId }),
    source: mapLogSource(input.source),
    ...(input.saveAsMemory ? { saveAsMemory: true } : {}),
    items,
  };
}

export type MealLogResult = { readonly ok: true } | { readonly ok: false; readonly error: string };

// Pure controller around the mutation (todo-21 pattern): success -> ok, and
// the caller clears the plate/closes/navigates; failure -> inline error and
// the review state stays exactly as the user left it (NO navigation).
export async function submitMealReview(
  logMeal: (args: LogMealBatchArgs) => Promise<unknown>,
  input: ReviewSubmission,
): Promise<MealLogResult> {
  const args = buildLogMealBatchArgs(input);
  try {
    await logMeal(args);
    console.log("[Frontend][MealReview] Meal logged", {
      date: args.date,
      mealType: args.mealType,
      itemCount: args.items.length,
      unknownKcalItems: args.items.filter((item) => item.kcalUnknown === true).length,
      saveAsMemory: args.saveAsMemory === true,
    });
    return { ok: true };
  } catch (error: unknown) {
    console.error("[Frontend][MealReview] logMealBatch failed", {
      date: args.date,
      itemCount: args.items.length,
      error: error instanceof Error ? error.message : String(error),
    });
    return {
      ok: false,
      error: "Could not log this meal. Your review is unchanged — check your connection and try again.",
    };
  }
}
